import { WebsiteBlock } from "../interfaces/WebsiteBlock";
import { createDefaultNavbarBlock } from "./navbarDefaults";

export interface NavbarExtractionResult {
  navbar: WebsiteBlock;
  htmlWithoutNavbar: string;
  extracted: boolean;
}

const parseStyleColor = (style: string | null | undefined, prop: string): string | undefined => {
  if (!style) return undefined;
  const match = style.match(new RegExp(`${prop}\\s*:\\s*([^;]+)`, "i"));
  return match?.[1]?.trim();
};

const serializeDocument = (doc: Document, originalHtml: string) => {
  const doctype = /<!DOCTYPE\s+html/i.test(originalHtml) ? "<!DOCTYPE html>\n" : "";
  return `${doctype}${doc.documentElement.outerHTML}`;
};

const findNavbarElement = (doc: Document): Element | null => {
  return (
    doc.querySelector("header.wp-navbar") ||
    doc.querySelector("nav.wp-navbar") ||
    doc.querySelector("header:has(nav)") ||
    doc.querySelector("header") ||
    doc.querySelector("nav[aria-label]") ||
    doc.querySelector("nav")
  );
};

const removeNavbarScripts = (doc: Document) => {
  doc.querySelectorAll("script").forEach((script) => {
    const content = script.textContent || "";
    if (/wp-nav-toggle|wp-nav-default|wp-navbar__nav/.test(content)) {
      script.remove();
    }
  });
};

export function extractNavbarFromHtml(html: string, brandName = "Mi Marca"): NavbarExtractionResult {
  if (!html.trim() || typeof DOMParser === "undefined") {
    return {
      navbar: createDefaultNavbarBlock(brandName),
      htmlWithoutNavbar: html,
      extracted: false,
    };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const navRoot = findNavbarElement(doc);

  if (!navRoot) {
    return {
      navbar: createDefaultNavbarBlock(brandName),
      htmlWithoutNavbar: html,
      extracted: false,
    };
  }

  const navContainer = navRoot.querySelector("nav, .wp-navbar__nav") || navRoot;
  const brandEl =
    navRoot.querySelector(".wp-navbar__brand span:last-child") ||
    navRoot.querySelector(".wp-navbar__brand") ||
    navRoot.querySelector("[class*='brand']");

  const logoEl = navRoot.querySelector(".wp-navbar__logo, [class*='logo']");
  const ctaEl = navRoot.querySelector(".wp-navbar__cta, a[class*='cta']");

  const links: { label: string; href: string }[] = [];
  navContainer.querySelectorAll("a").forEach((anchor) => {
    if (anchor === ctaEl || anchor.classList.contains("wp-navbar__cta")) return;
    if (anchor.closest(".wp-navbar__brand")) return;
    const label = anchor.textContent?.trim();
    if (!label) return;
    links.push({ label, href: anchor.getAttribute("href") || "#" });
  });

  const rootStyle = navRoot.getAttribute("style") || "";
  const navbar: WebsiteBlock = {
    ...createDefaultNavbarBlock(brandEl?.textContent?.trim() || brandName),
    id: `navbar-${Date.now()}`,
    brandName: brandEl?.textContent?.trim() || brandName,
    logoInitial: logoEl?.textContent?.trim().slice(0, 2) || (brandEl?.textContent?.trim() || brandName).charAt(0).toUpperCase(),
    links: links.length > 0 ? links : createDefaultNavbarBlock(brandName).links,
    ctaLabel: ctaEl?.textContent?.trim() || createDefaultNavbarBlock(brandName).ctaLabel,
    ctaHref: ctaEl?.getAttribute("href") || createDefaultNavbarBlock(brandName).ctaHref,
    backgroundColor: parseStyleColor(rootStyle, "background") || parseStyleColor(rootStyle, "background-color") || "#ffffff",
    textColor: parseStyleColor(rootStyle, "color") || "#0f172a",
  };

  navRoot.remove();
  removeNavbarScripts(doc);

  return {
    navbar,
    htmlWithoutNavbar: serializeDocument(doc, html),
    extracted: true,
  };
}
